/**
 * Enhanced conversions for Google Ads: the customer's email address, hashed here with SHA-256 so
 * that only the hash ever leaves the browser, pushed as `user_data` alongside the purchase event.
 * Google Ads matches the hash against signed-in Google accounts to attribute the sale; nothing
 * else reads it. It is only sent after the customer has accepted cookies (no tag fires otherwise)
 * and the next push clears it from the data layer again (analyticsService.ts FLAT_KEYS).
 *
 * Normalisation follows Google's rules for hashed email, so the hash matches Google's own: trim,
 * lower-case, and for gmail.com and googlemail.com addresses drop the dots in the local part.
 * Mirrors src/analytics/userData.ts in lpa.ui.
 */

export interface UserData {
  sha256_email_address: string;
}

const DOTLESS_DOMAINS = ['gmail.com', 'googlemail.com'];

// Google's normalised form of an address, or undefined when it is not one.
export const normaliseEmail = (email: string): string | undefined => {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) {
    return undefined;
  }

  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  return `${DOTLESS_DOMAINS.includes(domain) ? local.replace(/\./g, '') : local}@${domain}`;
};

export const sha256Hex = async (text: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

// The `user_data` for a purchase, or undefined without a usable address or Web Crypto.
export const emailUserData = async (email: string | null | undefined): Promise<UserData | undefined> => {
  if (typeof email !== 'string' || typeof crypto === 'undefined' || !crypto.subtle) {
    return undefined;
  }

  const normalised = normaliseEmail(email);
  if (!normalised) {
    return undefined;
  }

  return { sha256_email_address: await sha256Hex(normalised) };
};
